/**
 * Lead Stepper Component
 * ======================
 * Horizontal step progress navigation showing lead pipeline stages
 */

// The first step's key is `research`, and its label is not. The key is also
// the tab's value in the page URL, so saved links carrying `?tab=research`
// keep working. It is a historical name: since 2026-09-07 the step and the tab
// hold the due diligence report, not lead research. Renaming the key would
// break every saved link for no gain.
const STEPS = [
  { key: 'research', label: 'Due Diligence' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'questionnaire', label: 'Questionnaire' },
  { key: 'proposals', label: 'Proposals' },
];

const StepIcon = ({ stepKey, isActive, isCompleted, isInProgress }) => {
  const color = isActive
    ? isCompleted ? 'text-primary' : isInProgress ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'
    : isCompleted ? 'text-primary/70' : isInProgress ? 'text-amber-500' : 'text-gray-500 dark:text-gray-400';
  const cls = `w-8 h-8 ${color}`;

  const icons = {
    // A document with a folded corner. The step holds a produced report.
    research: (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 48 48">
        <path strokeLinejoin="round" strokeWidth={2} d="M12 5h16l10 10v28a1 1 0 01-1 1H13a1 1 0 01-1-1V6a1 1 0 011-1z" />
        <path strokeLinejoin="round" strokeWidth={2} d="M28 5v10h10" />
        <path strokeLinecap="round" strokeWidth={2} d="M19 24h14M19 31h14M19 38h8" opacity={0.6} />
      </svg>
    ),
    meetings: (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 48 48">
        <rect x="6" y="10" width="36" height="28" rx="3" strokeWidth={2} />
        <path strokeWidth={2} d="M6 18h36" />
        <path strokeLinecap="round" strokeWidth={2} d="M16 6v6M32 6v6" />
        <circle cx="17" cy="28" r="2.5" strokeWidth={1.5} opacity={0.5} />
        <circle cx="24" cy="28" r="2.5" strokeWidth={1.5} opacity={0.5} />
        <circle cx="31" cy="28" r="2.5" strokeWidth={1.5} opacity={0.5} />
      </svg>
    ),
    questionnaire: (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 48 48">
        <rect x="10" y="4" width="28" height="40" rx="3" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M18 16h12M18 22h8" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 30l3 3 6-6" opacity={0.6} />
        <path strokeLinecap="round" strokeWidth={1.5} d="M18 38h12" opacity={0.3} />
      </svg>
    ),
    proposals: (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 48 48">
        {/* Large gear */}
        <circle cx="18" cy="28" r="12" strokeWidth={2} />
        <circle cx="18" cy="28" r="5" strokeWidth={1.5} />
        <path strokeLinecap="round" strokeWidth={2} d="M18 14v3M18 40v3M4 28h3M30 28h3" />
        <path strokeLinecap="round" strokeWidth={2} d="M8.5 18.5l2.1 2.1M25.4 35.4l2.1 2.1M8.5 37.5l2.1-2.1M25.4 20.6l2.1-2.1" />
        {/* Small gear */}
        <circle cx="36" cy="14" r="8" strokeWidth={1.5} />
        <circle cx="36" cy="14" r="3.5" strokeWidth={1.2} />
        <path strokeLinecap="round" strokeWidth={1.5} d="M36 4v2.5M36 22v2.5M26 14h2.5M43.5 14H46" />
        <path strokeLinecap="round" strokeWidth={1.5} d="M29.3 7.3l1.8 1.8M41 19l1.8 1.8M29.3 20.7l1.8-1.8M41 9l1.8-1.8" />
      </svg>
    ),
  };

  return icons[stepKey] || null;
};

const CompletionBadge = () => (
  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow-md bg-gradient-to-br from-emerald-400 to-emerald-600 ring-2 ring-white dark:ring-gray-800">
    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  </div>
);

const StepConnector = ({ isPast }) => (
  <div className="flex items-center shrink-0 mx-0.5">
    <svg className={`w-5 h-5 transition-colors duration-200 ${isPast ? 'text-primary/40' : 'text-gray-200 dark:text-gray-700'}`} viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 2l14 8-14 8V2z" />
    </svg>
  </div>
);

const LeadStepper = ({ activeTab, onTabChange, completionStatus }) => {
  const activeIndex = STEPS.findIndex(s => s.key === activeTab);

  return (
    <div className="flex items-center gap-1 sm:gap-3 px-1">
      {STEPS.map((step, index) => {
        const isActive = step.key === activeTab;
        const isCompleted = completionStatus[step.key] === 'completed';
        const isInProgress = completionStatus[step.key] === 'in_progress';
        const isLast = index === STEPS.length - 1;

        return (
          <div key={step.key} className="contents">
            <button
              onClick={() => onTabChange(step.key)}
              className={`
                group relative flex items-center justify-center sm:justify-start gap-2 sm:gap-3 py-3 px-2 sm:px-5 rounded-lg transition-all duration-200 flex-1 cursor-pointer
                ${isActive
                  ? isCompleted
                    ? 'border-2 border-primary bg-primary/15 shadow-md'
                    : isInProgress
                      ? 'border-2 border-amber-500 dark:border-amber-400 bg-amber-50 dark:bg-amber-900/20 shadow-md'
                      : 'border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 shadow-md'
                  : isCompleted
                    ? 'border bg-primary/5 border-primary/30 shadow-sm hover:bg-primary/[0.07]'
                    : isInProgress
                      ? 'border bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600 shadow-sm hover:bg-amber-100/70'
                      : 'border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }
              `}
            >
              {isCompleted && <CompletionBadge />}

              <StepIcon stepKey={step.key} isActive={isActive} isCompleted={isCompleted} isInProgress={isInProgress} />

              <span
                className={`
                  text-sm whitespace-nowrap transition-colors duration-200 hidden sm:inline
                  ${isCompleted
                    ? 'text-primary font-semibold'
                    : isInProgress
                      ? 'text-amber-700 dark:text-amber-400 font-semibold'
                      : isActive
                        ? 'text-gray-800 dark:text-gray-200 font-semibold'
                        : 'text-gray-500 dark:text-gray-400 font-medium group-hover:text-gray-700'
                  }
                `}
              >
                {step.label}
              </span>
            </button>

            {!isLast && <StepConnector isPast={isCompleted || index < activeIndex} />}
          </div>
        );
      })}
    </div>
  );
};

export { STEPS };
export default LeadStepper;
